import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { AppShell } from "../components/cod/Layout";
import { Seo } from "../components/cod/Seo";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { oicApi } from "../lib/api";
import { toast } from "sonner";
import {
  Brain, Activity, AlertTriangle, DollarSign, TrendingUp, Users, Zap, Search,
  Play, ArrowLeft, CheckCircle2, XCircle, Archive, Globe, FileText, Share2, ExternalLink,
  Sliders, ShieldCheck, Rss, RefreshCw, Trash2, Plus, Target, ListChecks, LineChart,
  Sparkles, History, CalendarClock,
} from "lucide-react";

const URGENCY_STYLES = {
  red:    "bg-red-100 text-red-800 border-red-300",
  yellow: "bg-amber-100 text-amber-800 border-amber-300",
  green:  "bg-emerald-100 text-emerald-800 border-emerald-300",
};
const STATUS_STYLES = {
  pending:   "bg-slate-100 text-slate-700",
  approved:  "bg-blue-100 text-blue-800",
  published: "bg-emerald-100 text-emerald-800",
  scheduled: "bg-purple-100 text-purple-800",
  archived:  "bg-slate-200 text-slate-500",
  rejected:  "bg-red-100 text-red-800",
};
const AGENT_LABELS = {
  grant: "Grant", procurement: "Procurement", employer: "Employer",
  economic_development: "Econ. Dev.", legislative: "Legislation",
  workforce: "Workforce", community: "Community",
};

const QUEUES = [
  { key: "pending", label: "Pending Review", icon: AlertTriangle },
  { key: "executive_priority", label: "Executive Priority", icon: Zap, queue: true },
  { key: "grant_pursuit", label: "Grant Pursuit", icon: DollarSign, queue: true },
  { key: "crm", label: "CRM Queue", icon: Users, queue: true },
  { key: "publishing", label: "Publishing", icon: Globe, queue: true },
  { key: "social", label: "Social Queue", icon: Share2, queue: true },
  { key: "approved", label: "Approved", icon: CheckCircle2 },
  { key: "published", label: "Published", icon: Globe },
  { key: "scheduled", label: "Scheduled", icon: Activity },
  { key: "archived", label: "Archived", icon: Archive },
  { key: "rejected", label: "Rejected", icon: XCircle },
];

// ═══════════════════════════════════════════════════════════════════════════
// Main Dashboard
// ═══════════════════════════════════════════════════════════════════════════
export function IntelligenceDashboard() {
  const [queues, setQueues] = useState({});
  const [rows, setRows] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [agentFilter, setAgentFilter] = useState("all");
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const q = await oicApi.queues();
      setQueues(q);
      const params = {};
      const spec = QUEUES.find(x => x.key === activeTab);
      if (spec?.queue) params.queue = activeTab;
      else params.status = activeTab;
      if (agentFilter !== "all") params.agent_type = agentFilter;
      if (search.trim()) params.search = search.trim();
      const items = await oicApi.list(params);
      setRows(items);
    } catch (e) { toast.error("Failed to load intelligence data"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTab, agentFilter]);

  const runAll = async () => {
    setRunning(true);
    try {
      const r = await oicApi.runAll();
      toast.success(`Scan complete — ${r.new} new · ${r.classified} classified · ${r.sources_run} sources`);
      load();
    } catch (e) { toast.error("Scan failed"); }
    finally { setRunning(false); }
  };

  return (
    <AppShell>
      <Seo title="Opportunity Intelligence Center" path="/staff/intelligence" noindex/>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--cod-red)] font-semibold">Institutional Intelligence</div>
          <h1 className="font-serif text-3xl text-slate-900 mt-1 flex items-center gap-2">
            <Brain size={24} className="text-[var(--cod-navy)]"/> Opportunity Intelligence Center
          </h1>
          <div className="cod-rule-red mt-3"/>
          <p className="text-sm text-slate-600 mt-3 max-w-3xl">
            AI-powered intelligence platform that continuously discovers, scores, and prioritizes opportunities benefiting Chicago residents, employers, businesses, and Chicago Opportunity Desk. Every item is evaluated against a single question: <em>&quot;How can COD create opportunity from this?&quot;</em>
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2 flex-wrap justify-end">
            <Link to="/staff/intelligence/exec-dashboard"><Button size="sm" variant="outline" data-testid="oic-btn-exec-dashboard"><Zap size={14} className="mr-1"/>Exec Dashboard</Button></Link>
            <Link to="/staff/intelligence/exec-brief"><Button size="sm" variant="outline" data-testid="oic-btn-exec-brief"><Sparkles size={14} className="mr-1"/>AI Brief</Button></Link>
            <Link to="/staff/intelligence/forecast"><Button size="sm" variant="outline" data-testid="oic-btn-forecast"><CalendarClock size={14} className="mr-1"/>Forecast</Button></Link>
            <Link to="/staff/intelligence/institutional-memory"><Button size="sm" variant="outline" data-testid="oic-btn-memory"><History size={14} className="mr-1"/>Memory</Button></Link>
            <Link to="/staff/intelligence/rules"><Button size="sm" variant="outline" data-testid="oic-btn-rules"><ShieldCheck size={14} className="mr-1"/>Rules</Button></Link>
            <Link to="/staff/intelligence/thresholds"><Button size="sm" variant="outline" data-testid="oic-btn-thresholds"><Sliders size={14} className="mr-1"/>Thresholds</Button></Link>
            <Link to="/staff/intelligence/sources"><Button size="sm" variant="outline" data-testid="oic-btn-sources"><Rss size={14} className="mr-1"/>Sources</Button></Link>
            <Link to="/staff/intelligence/analytics"><Button size="sm" variant="outline" data-testid="oic-btn-analytics"><TrendingUp size={14} className="mr-1"/>Analytics</Button></Link>
          </div>
          <Button className="cod-bg-red" onClick={runAll} disabled={running} data-testid="oic-btn-run-all">
            {running ? <><RefreshCw size={14} className="mr-1 animate-spin"/>Scanning…</> : <><Play size={14} className="mr-1"/>Run All Agents Now</>}
          </Button>
        </div>
      </div>

      {/* Queue tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4" data-testid="oic-queue-tiles">
        {QUEUES.slice(0, 6).map(q => (
          <button key={q.key} onClick={() => setActiveTab(q.key)}
            data-testid={`oic-tile-${q.key}`}
            className={`rounded-lg border p-3 text-left hover:border-[var(--cod-red)] transition-colors ${activeTab === q.key ? "border-[var(--cod-red)] bg-[var(--cod-red)]/5" : "border-slate-200 bg-white"}`}>
            <div className="flex items-center justify-between">
              <q.icon size={14} className="text-[var(--cod-navy)]"/>
              <span className="text-2xl font-serif text-slate-900">{queues[q.key] ?? "—"}</span>
            </div>
            <div className="text-xs text-slate-600 mt-1">{q.label}</div>
          </button>
        ))}
      </div>

      {/* Sub tabs */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex-wrap h-auto">
            {QUEUES.map(q => (
              <TabsTrigger key={q.key} value={q.key} data-testid={`oic-tab-${q.key}`} className="text-xs">
                {q.label} <span className="ml-1 text-[10px] opacity-70">{queues[q.key] ?? 0}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Filter row */}
      <div className="grid sm:grid-cols-3 gap-2 mb-4">
        <div className="relative sm:col-span-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input placeholder="Search headline, COD relevance, agency…" value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") load(); }} className="pl-9" data-testid="oic-search"/>
        </div>
        <Select value={agentFilter} onValueChange={setAgentFilter}>
          <SelectTrigger data-testid="oic-agent-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Agents</SelectItem>
            {Object.entries(AGENT_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Opportunity list */}
      <div className="space-y-2" data-testid="oic-list">
        {rows.length === 0 && (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-8 text-center">
            No items in this queue. Try running all agents to discover new opportunities.
          </div>
        )}
        {rows.map(o => (
          <OpportunityRow key={o.id} opp={o}/>
        ))}
      </div>
    </AppShell>
  );
}

function OpportunityRow({ opp }) {
  const scoreColor = opp.opportunity_score >= 80 ? "text-emerald-700"
                     : opp.opportunity_score >= 65 ? "text-amber-700" : "text-slate-500";
  return (
    <Link to={`/staff/intelligence/${opp.id}`} data-testid={`oic-row-${opp.id}`}
      className="block rounded-lg border border-slate-200 bg-white p-4 hover:border-[var(--cod-red)] transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${URGENCY_STYLES[opp.urgency_level] || URGENCY_STYLES.green}`}>
              {opp.urgency_level}
            </span>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${STATUS_STYLES[opp.status]}`}>{opp.status}</span>
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-slate-100 text-slate-700">{opp.opportunity_type}</span>
            {opp.financial_benefit_to_cod && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-[var(--cod-red)] text-white flex items-center gap-1"><DollarSign size={10}/>$ to COD</span>
            )}
            {opp.passes_threshold && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-600 text-white">Above threshold</span>
            )}
          </div>
          <div className="font-semibold text-slate-900 leading-snug">{opp.article_headline}</div>
          <div className="text-xs text-slate-500 mt-1">
            {opp.source_name} · {opp.cod_relevance} · {opp.agency || "—"}
            {opp.deadline_at && <> · Deadline: <strong>{String(opp.deadline_at).slice(0, 10)}</strong></>}
            {opp.funding_amount && <> · <strong>{opp.funding_amount}</strong></>}
          </div>
          <p className="text-sm text-slate-700 mt-2 line-clamp-2">{opp.cod_relevance_summary}</p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <div className={`text-2xl font-serif ${scoreColor}`}>{opp.opportunity_score}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Opp Score</div>
          {opp.opportunity_type === "Grant" && (
            <>
              <div className="text-lg font-serif text-slate-700 mt-1">{opp.grant_pursuit_score}</div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Grant Pursuit</div>
            </>
          )}
          {typeof opp.strategic_alignment_score === "number" && opp.strategic_alignment_score > 0 && (
            <>
              <div className="text-lg font-serif text-[var(--cod-navy)] mt-1 flex items-center gap-1">
                <Target size={12}/>{opp.strategic_alignment_score}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Strategic</div>
            </>
          )}
          <div className="text-[10px] text-slate-500 mt-1">Conf: {opp.confidence_score}%</div>
        </div>
      </div>
    </Link>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Opportunity Detail
// ═══════════════════════════════════════════════════════════════════════════
export function IntelligenceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [opp, setOpp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");
  const [reasons, setReasons] = useState([]);
  const [editHeadline, setEditHeadline] = useState("");
  const [editMeta, setEditMeta] = useState("");
  const [editBody, setEditBody] = useState("");
  const load = () => oicApi.get(id).then(o => {
    setOpp(o);
    setEditHeadline(o.article_headline);
    setEditMeta(o.seo_meta_description);
    setEditBody(o.article_body_md);
  });
  useEffect(() => { load(); oicApi.reasons().then(setReasons).catch(() => {}); /* eslint-disable-next-line */ }, [id]);
  if (!opp) return <AppShell><div className="text-sm text-slate-500">Loading…</div></AppShell>;

  const action = async (act, msg) => {
    if (act === "reject") { setRejectOpen(true); return; }
    setBusy(true);
    try { await oicApi.action(id, act); toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const submitReject = async () => {
    if (!rejectReason) { toast.error("Please select a rejection reason"); return; }
    setBusy(true);
    try {
      await oicApi.action(id, "reject", { reason: rejectReason, notes: rejectNotes });
      toast.success("Rejected — decision logged for AI learning");
      setRejectOpen(false); setRejectReason(""); setRejectNotes("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setBusy(false); }
  };
  const doPublish = async () => {
    setBusy(true);
    try {
      await oicApi.publish(id, {
        article_body: editBody, seo_title: editHeadline, meta_description: editMeta,
      });
      toast.success("Published to Opportunity Center");
      setPublishOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
    finally { setBusy(false); }
  };
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text); toast.success(`${label} copied`);
  };

  return (
    <AppShell>
      <Seo title={opp.article_headline} path={`/staff/intelligence/${id}`} noindex/>
      <button onClick={() => navigate("/staff/intelligence")}
        className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-3">
        <ArrowLeft size={12}/> Intelligence Center
      </button>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${URGENCY_STYLES[opp.urgency_level] || URGENCY_STYLES.green}`}>{opp.urgency_level}</span>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${STATUS_STYLES[opp.status]}`}>{opp.status}</span>
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-slate-100 text-slate-700">{opp.opportunity_type}</span>
              {opp.financial_benefit_to_cod && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-[var(--cod-red)] text-white">$ to COD</span>}
            </div>
            <h1 className="font-serif text-2xl text-slate-900" data-testid="oic-detail-title">{opp.article_headline}</h1>
            <div className="cod-rule-red mt-3"/>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">Why this matters for COD</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-slate-800 leading-relaxed" data-testid="oic-cod-relevance">{opp.cod_relevance_summary}</p>
              <div className="mt-3 text-xs text-slate-500">Relevance: <span className="text-slate-800 font-semibold">{opp.cod_relevance}</span></div>
              {opp.recommended_actions?.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Recommended actions</div>
                  <ul className="text-sm text-slate-800 space-y-1 list-disc list-inside">
                    {opp.recommended_actions.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {opp.cod_next_steps?.length > 0 && (
            <Card className="border-[var(--cod-navy)]/30">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ListChecks size={16} className="text-[var(--cod-navy)]"/> COD Next Steps
                  <span className="text-xs text-slate-500 font-normal">— AI-generated action plan</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="text-sm text-slate-800 space-y-2" data-testid="oic-next-steps">
                  {opp.cod_next_steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--cod-navy)] text-white text-xs font-semibold flex items-center justify-center mt-0.5">{i + 1}</span>
                      <span className="leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="text-base">Article Draft (SEO-ready)</CardTitle></CardHeader>
            <CardContent>
              <div className="mb-2"><Label className="text-xs">Headline</Label><Input value={editHeadline} onChange={e=>setEditHeadline(e.target.value)} data-testid="oic-edit-headline"/></div>
              <div className="mb-2"><Label className="text-xs">Meta description (≤160 chars)</Label><Input value={editMeta} onChange={e=>setEditMeta(e.target.value)} data-testid="oic-edit-meta"/></div>
              <div><Label className="text-xs">Article body (Markdown)</Label>
                <Textarea rows={12} value={editBody} onChange={e=>setEditBody(e.target.value)} className="font-mono text-xs" data-testid="oic-edit-body"/>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center justify-between">Social Drafts <span className="text-xs text-slate-500 font-normal">Copy & paste — human approval required</span></CardTitle></CardHeader>
            <CardContent className="space-y-3" data-testid="oic-social">
              {Object.entries(opp.social_drafts || {}).map(([platform, text]) => (
                <div key={platform} className="border border-slate-200 rounded p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-widest font-semibold text-slate-600">{platform}</span>
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(text, platform)} data-testid={`oic-copy-${platform}`}>Copy</Button>
                  </div>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{text}</p>
                </div>
              ))}
              {(!opp.social_drafts || Object.keys(opp.social_drafts).length === 0) && (
                <div className="text-sm text-slate-500">No social drafts available.</div>
              )}
            </CardContent>
          </Card>

          {opp.master_rule_violations?.length > 0 && (
            <Card className="border-red-300 bg-red-50/40">
              <CardHeader><CardTitle className="text-base text-red-700 flex items-center gap-2"><AlertTriangle size={16}/> Master rule violations</CardTitle></CardHeader>
              <CardContent>
                <ul className="text-sm text-red-800 list-disc list-inside">
                  {opp.master_rule_violations.map((v, i) => <li key={i}>{v}</li>)}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Scores</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <ScoreBar label="Opportunity Score" value={opp.opportunity_score} max={100}/>
              {opp.opportunity_type === "Grant" && (
                <ScoreBar label="Grant Pursuit Score" value={opp.grant_pursuit_score} max={100}/>
              )}
              {typeof opp.strategic_alignment_score === "number" && (
                <ScoreBar label="Strategic Alignment" value={opp.strategic_alignment_score} max={100}/>
              )}
              <ScoreBar label="Confidence" value={opp.confidence_score} max={100}/>
              <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                <div><span className="font-semibold">{opp.confidence_level}</span></div>
                {opp.confidence_reasoning && <div className="mt-1">{opp.confidence_reasoning}</div>}
              </div>
              <div className="text-xs pt-2 border-t border-slate-100">
                <div className="text-slate-500">Threshold used: <strong>{opp.threshold_used}</strong> ({opp.threshold_key})</div>
                <div className="text-slate-500">Passes threshold: <strong>{opp.passes_threshold ? "Yes" : "No"}</strong></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2">
              {opp.agency && <div><span className="text-slate-500">Agency:</span> <span className="text-slate-900">{opp.agency}</span></div>}
              {opp.deadline_at && <div><span className="text-slate-500">Deadline:</span> <span className="text-slate-900 font-semibold">{String(opp.deadline_at).slice(0, 10)}</span></div>}
              {opp.funding_amount && <div><span className="text-slate-500">Funding:</span> <span className="text-slate-900">{opp.funding_amount}</span></div>}
              {opp.eligibility_notes && <div><span className="text-slate-500">Eligibility:</span> <span className="text-slate-900">{opp.eligibility_notes}</span></div>}
              {opp.audiences?.length > 0 && (
                <div><span className="text-slate-500">Audiences:</span> <span className="text-slate-900">{opp.audiences.join(", ")}</span></div>
              )}
              {opp.urgency_factors?.length > 0 && (
                <div><span className="text-slate-500">Urgency factors:</span>
                  <ul className="text-xs list-disc list-inside mt-1">{opp.urgency_factors.map((u, i) => <li key={i}>{u}</li>)}</ul>
                </div>
              )}
              <div className="pt-2 border-t border-slate-100">
                <div className="text-slate-500 text-xs">Source</div>
                <div className="text-slate-900">{opp.source_name} <Badge variant="outline" className="ml-1 text-[10px]">{opp.source_reliability}</Badge></div>
                <a href={opp.raw_url} target="_blank" rel="noopener noreferrer" className="text-xs cod-link flex items-center gap-1 mt-1">
                  View original <ExternalLink size={10}/>
                </a>
              </div>
            </CardContent>
          </Card>

          {opp.crm_matches?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users size={14}/>CRM Matches ({opp.crm_matches.length})</CardTitle></CardHeader>
              <CardContent className="text-xs space-y-1">
                {opp.crm_matches.slice(0, 5).map((m, i) => (
                  <Link key={i} to={`/staff/bd/organizations/${m.organization_id}`} className="block hover:text-[var(--cod-red)]">
                    · <strong>{m.organization_name}</strong> — {m.reason}
                  </Link>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <Card>
            <CardHeader><CardTitle className="text-base">Actions</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {opp.status === "pending" && !opp.master_rule_violations?.length && (
                <>
                  <Button className="w-full cod-bg-red" onClick={() => action("approve", "Approved")} disabled={busy} data-testid="oic-approve">
                    <CheckCircle2 size={14} className="mr-1"/>Approve
                  </Button>
                  <Button className="w-full" variant="outline" onClick={() => action("reject", "Rejected")} disabled={busy} data-testid="oic-reject">
                    <XCircle size={14} className="mr-1"/>Reject
                  </Button>
                </>
              )}
              {opp.status === "approved" && (
                <Button className="w-full cod-bg-red" onClick={() => setPublishOpen(true)} disabled={busy} data-testid="oic-publish-btn">
                  <Globe size={14} className="mr-1"/>Publish to Opportunity Center
                </Button>
              )}
              {(opp.status === "pending" || opp.status === "approved") && (
                <Button className="w-full" variant="outline" onClick={() => action("archive", "Archived")} disabled={busy} data-testid="oic-archive">
                  <Archive size={14} className="mr-1"/>Archive
                </Button>
              )}
              {opp.status === "archived" && (
                <Button className="w-full" variant="outline" onClick={() => action("restore", "Restored")} disabled={busy} data-testid="oic-restore">Restore to Pending</Button>
              )}
              {opp.status === "published" && (
                <Link to={`/opportunities/${opp.slug}`} target="_blank"><Button className="w-full" variant="outline"><Globe size={14} className="mr-1"/>View public page</Button></Link>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Publish to Opportunity Center</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>This will publish the article to <strong>joincod.org/opportunities/{opp.slug}</strong> using the current draft.</p>
            <p className="text-xs text-slate-500">You can still edit content after publishing.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPublishOpen(false)}>Cancel</Button>
            <Button className="cod-bg-red" onClick={doPublish} disabled={busy} data-testid="oic-confirm-publish">Publish now</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Opportunity</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">Rejection reason is required. This decision trains COD&apos;s AI to prioritize better opportunities going forward.</p>
            <div>
              <Label className="text-xs">Reason <span className="text-red-600">*</span></Label>
              <Select value={rejectReason} onValueChange={setRejectReason}>
                <SelectTrigger data-testid="oic-reject-reason">
                  <SelectValue placeholder="Select a reason…"/>
                </SelectTrigger>
                <SelectContent>
                  {reasons.map(r => <SelectItem key={r} value={r} data-testid={`oic-reject-reason-${r.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Additional notes (optional)</Label>
              <Textarea rows={3} value={rejectNotes} onChange={e => setRejectNotes(e.target.value)}
                placeholder="Add context to help the AI learn from this decision…" data-testid="oic-reject-notes"/>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
              <Button className="cod-bg-red" onClick={submitReject} disabled={busy || !rejectReason} data-testid="oic-reject-submit">
                {busy ? "Rejecting…" : "Confirm Rejection"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function ScoreBar({ label, value, max = 100 }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const color = pct >= 80 ? "bg-emerald-500" : pct >= 65 ? "bg-amber-500" : "bg-slate-400";
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-600">{label}</span>
        <span className="text-slate-900 font-semibold">{value}/{max}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Rules editor
// ═══════════════════════════════════════════════════════════════════════════
export function IntelligenceRules() {
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { oicApi.rules().then(setRules); }, []);
  const save = async () => {
    setBusy(true);
    try { await oicApi.updateRules(rules); toast.success("Master rules saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <AppShell>
      <Seo title="OIC · Master Rules" path="/staff/intelligence/rules" noindex/>
      <Link to="/staff/intelligence" className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-3"><ArrowLeft size={12}/> Intelligence Center</Link>
      <h1 className="font-serif text-2xl text-slate-900 flex items-center gap-2"><ShieldCheck size={20} className="text-[var(--cod-navy)]"/> Master Rules</h1>
      <div className="cod-rule-red mt-3 mb-4"/>
      <p className="text-sm text-slate-600 mb-4 max-w-3xl">These rules are injected into every AI classification. Any item matching a rule is auto-rejected. Editable by admins only.</p>
      <div className="space-y-2 mb-4" data-testid="oic-rules-list">
        {rules.map((r, i) => (
          <div key={i} className="flex gap-2">
            <Input value={r} onChange={e => { const copy = [...rules]; copy[i] = e.target.value; setRules(copy); }} data-testid={`oic-rule-${i}`}/>
            <Button variant="outline" size="sm" onClick={() => setRules(rules.filter((_, j) => j !== i))}><Trash2 size={14}/></Button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => setRules([...rules, ""])} data-testid="oic-add-rule"><Plus size={14} className="mr-1"/>Add rule</Button>
        <Button className="cod-bg-red" onClick={save} disabled={busy} data-testid="oic-save-rules">{busy ? "Saving…" : "Save master rules"}</Button>
      </div>
    </AppShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Thresholds
// ═══════════════════════════════════════════════════════════════════════════
export function IntelligenceThresholds() {
  const [t, setT] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { oicApi.thresholds().then(setT); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(t).filter(([k]) => k !== "updated_at" && k !== "updated_by" && k !== "id"));
      await oicApi.updateThresholds(payload);
      toast.success("Thresholds saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const keys = [
    ["grants", "Grants — trigger executive alert"],
    ["procurement", "Procurement — trigger executive alert"],
    ["employer_partnerships", "Employer Partnerships"],
    ["economic_development", "Economic Development"],
    ["workforce_programs", "Workforce Programs"],
    ["community_resources", "Community Resources"],
    ["public_news", "Public News"],
  ];
  return (
    <AppShell>
      <Seo title="OIC · Thresholds" path="/staff/intelligence/thresholds" noindex/>
      <Link to="/staff/intelligence" className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-3"><ArrowLeft size={12}/> Intelligence Center</Link>
      <h1 className="font-serif text-2xl text-slate-900 flex items-center gap-2"><Sliders size={20} className="text-[var(--cod-navy)]"/> Priority Thresholds</h1>
      <div className="cod-rule-red mt-3 mb-4"/>
      <p className="text-sm text-slate-600 mb-4 max-w-3xl">Opportunities with scores below the threshold do not trigger executive alerts and are archived from the priority queue (but remain searchable).</p>
      <div className="space-y-3 max-w-lg" data-testid="oic-thresholds-form">
        {keys.map(([k, l]) => (
          <div key={k}>
            <Label>{l}</Label>
            <div className="flex items-center gap-3">
              <Input type="range" min="0" max="100" value={t[k] ?? 70} onChange={e => setT({ ...t, [k]: parseInt(e.target.value, 10) })} className="flex-1" data-testid={`oic-thresh-${k}`}/>
              <span className="text-lg font-serif w-12 text-right">{t[k] ?? 70}</span>
            </div>
          </div>
        ))}
        <Button className="cod-bg-red mt-4" onClick={save} disabled={busy} data-testid="oic-save-thresholds">{busy ? "Saving…" : "Save thresholds"}</Button>
      </div>
    </AppShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Sources
// ═══════════════════════════════════════════════════════════════════════════
export function IntelligenceSources() {
  const [sources, setSources] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [f, setF] = useState({ name: "", agent_type: "community", source_type: "rss", url: "", reliability: "community_org", active: true, scan_frequency_hours: 6 });
  const load = () => oicApi.sources().then(setSources);
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await oicApi.createSource(f); toast.success("Source added"); setShowNew(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (s) => {
    try { await oicApi.patchSource(s.id, { active: !s.active }); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this source?")) return;
    try { await oicApi.deleteSource(id); load(); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <AppShell>
      <Seo title="OIC · Sources" path="/staff/intelligence/sources" noindex/>
      <Link to="/staff/intelligence" className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-3"><ArrowLeft size={12}/> Intelligence Center</Link>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="font-serif text-2xl text-slate-900 flex items-center gap-2"><Rss size={20} className="text-[var(--cod-navy)]"/> Sources</h1>
          <div className="cod-rule-red mt-3"/>
        </div>
        <Button className="cod-bg-red" onClick={() => setShowNew(true)} data-testid="oic-add-source"><Plus size={14} className="mr-1"/>Add Source</Button>
      </div>
      <div className="space-y-2" data-testid="oic-sources-list">
        {sources.map(s => (
          <div key={s.id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900">{s.name}</span>
                <Badge variant="outline" className="text-[10px]">{s.agent_type}</Badge>
                <Badge variant="outline" className="text-[10px]">{s.reliability}</Badge>
                {!s.active && <Badge className="text-[10px] bg-slate-200 text-slate-600">Paused</Badge>}
              </div>
              <div className="text-xs text-slate-500 mt-1 truncate">{s.url}</div>
              {s.last_scan_at && <div className="text-[10px] text-slate-500 mt-1">Last scan: {String(s.last_scan_at).slice(0, 16).replace("T", " ")} — {s.last_scan_status}</div>}
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <Button size="sm" variant="outline" onClick={() => toggle(s)} data-testid={`oic-toggle-${s.id}`}>{s.active ? "Pause" : "Resume"}</Button>
              <Button size="sm" variant="outline" onClick={() => del(s.id)}><Trash2 size={12}/></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Intelligence Source</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={f.name} onChange={e=>setF({...f, name:e.target.value})} data-testid="oic-newsrc-name"/></div>
            <div><Label>URL</Label><Input value={f.url} onChange={e=>setF({...f, url:e.target.value})} data-testid="oic-newsrc-url"/></div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Agent</Label>
                <Select value={f.agent_type} onValueChange={v=>setF({...f, agent_type:v})}>
                  <SelectTrigger data-testid="oic-newsrc-agent"><SelectValue/></SelectTrigger>
                  <SelectContent>{Object.entries(AGENT_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={f.source_type} onValueChange={v=>setF({...f, source_type:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rss">RSS / Atom</SelectItem>
                    <SelectItem value="grants_gov_api">Grants.gov API</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Reliability</Label>
              <Select value={f.reliability} onValueChange={v=>setF({...f, reliability:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="government">Government</SelectItem>
                  <SelectItem value="foundation">Foundation</SelectItem>
                  <SelectItem value="major_news">Major News</SelectItem>
                  <SelectItem value="industry_pub">Industry Publication</SelectItem>
                  <SelectItem value="community_org">Community Organization</SelectItem>
                  <SelectItem value="social_media">Social Media</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="cod-bg-red w-full" onClick={create} data-testid="oic-newsrc-submit">Add Source</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Analytics
// ═══════════════════════════════════════════════════════════════════════════
function OICStat({ label, value, sub }) {
  return (
    <Card><CardContent className="pt-6"><div className="text-xs uppercase tracking-widest text-slate-500">{label}</div><div className="text-3xl font-serif text-slate-900 mt-1">{value ?? 0}</div>{sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}</CardContent></Card>
  );
}

export function IntelligenceAnalytics() {
  const [a, setA] = useState(null);
  useEffect(() => { oicApi.analytics().then(setA); }, []);
  if (!a) return <AppShell><div className="text-sm text-slate-500">Loading…</div></AppShell>;
  return (
    <AppShell>
      <Seo title="OIC · Analytics" path="/staff/intelligence/analytics" noindex/>
      <Link to="/staff/intelligence" className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-3"><ArrowLeft size={12}/> Intelligence Center</Link>
      <h1 className="font-serif text-2xl text-slate-900 flex items-center gap-2"><TrendingUp size={20} className="text-[var(--cod-navy)]"/> Analytics</h1>
      <div className="cod-rule-red mt-3 mb-4"/>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="oic-analytics">
        <OICStat label="Total Opportunities" value={a.total_opportunities}/>
        <OICStat label="New This Week" value={a.new_this_week} sub={`${a.new_this_month || 0} this month`}/>
        <OICStat label="Pending Review" value={a.pending_review}/>
        <OICStat label="Published" value={a.published}/>
        <OICStat label="Grants Identified" value={a.grants_identified} sub={`${a.grants_high_pursuit || 0} high pursuit`}/>
        <OICStat label="Procurement" value={a.procurement_identified}/>
        <OICStat label="Red Urgency" value={a.red_urgency_open} sub="Currently open"/>
        <OICStat label="Executive Alerts (week)" value={a.executive_alerts_this_week} sub={`${a.executive_alerts_total || 0} lifetime`}/>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Card><CardHeader><CardTitle className="text-base">Score Distribution</CardTitle></CardHeader><CardContent>
          {Object.entries(a.score_distribution || {}).map(([bucket, n]) => (
            <div key={bucket} className="flex items-center gap-3 mb-2">
              <span className="text-xs text-slate-500 w-16">{bucket}</span>
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-[var(--cod-navy)]" style={{ width: `${Math.min(100, (n / Math.max(1, a.total_opportunities)) * 100 * 3)}%` }}/>
              </div>
              <span className="text-sm text-slate-800 w-8 text-right">{n}</span>
            </div>
          ))}
        </CardContent></Card>
        <Card><CardHeader><CardTitle className="text-base">By Opportunity Type (30d)</CardTitle></CardHeader><CardContent>
          {(a.by_type || []).map(([t, n]) => (
            <div key={t} className="flex items-center gap-3 mb-2">
              <span className="text-xs text-slate-700 w-32 truncate">{t}</span>
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-[var(--cod-red)]" style={{ width: `${Math.min(100, (n / Math.max(1, (a.by_type || [[null, 1]])[0][1])) * 100)}%` }}/>
              </div>
              <span className="text-sm text-slate-800 w-8 text-right">{n}</span>
            </div>
          ))}
        </CardContent></Card>
      </div>
    </AppShell>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Executive Dashboard (OIC Enhancements Part 2)
// ═══════════════════════════════════════════════════════════════════════════
export function IntelligenceExecDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { oicApi.execDashboard().then(setD).catch(() => toast.error("Failed to load exec dashboard")); }, []);
  if (!d) return <AppShell><div className="text-sm text-slate-500">Loading…</div></AppShell>;
  const momentumColor = d.momentum.delta_pct >= 0 ? "text-emerald-600" : "text-red-600";
  return (
    <AppShell>
      <Seo title="OIC · Executive Dashboard" path="/staff/intelligence/exec-dashboard" noindex/>
      <Link to="/staff/intelligence" className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-3"><ArrowLeft size={12}/> Intelligence Center</Link>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 className="font-serif text-2xl text-slate-900 flex items-center gap-2"><Zap size={20} className="text-[var(--cod-red)]"/> Executive Dashboard</h1>
          <div className="cod-rule-red mt-3"/>
          <p className="text-sm text-slate-600 mt-3 max-w-3xl">A leadership-oriented view of COD&apos;s institutional intelligence — highest-value opportunities, momentum, and pipeline health.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="oic-exec-kpis">
        <OICStat label="Total Opportunities" value={d.kpis.total_opportunities}/>
        <OICStat label="Pending Review" value={d.kpis.pending_review}/>
        <OICStat label="Above Threshold" value={d.kpis.above_threshold_open} sub="Open"/>
        <OICStat label="Red Urgency" value={d.kpis.red_urgency_open} sub="Open"/>
        <OICStat label="Grants (High Pursuit)" value={d.kpis.grants_high_pursuit_open} sub="Open, ≥80"/>
        <OICStat label="Published (30d)" value={d.kpis.published_this_month}/>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Activity size={14}/>Momentum</CardTitle></CardHeader>
          <CardContent className="text-sm">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-serif text-slate-900">{d.momentum.this_week}</span>
              <span className={`text-sm font-semibold ${momentumColor}`}>
                {d.momentum.delta_pct >= 0 ? "+" : ""}{d.momentum.delta_pct}%
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-1">this week vs {d.momentum.previous_week} last week</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><CheckCircle2 size={14}/>Decision Velocity (30d)</CardTitle></CardHeader>
          <CardContent className="text-sm">
            <div className="text-3xl font-serif text-slate-900">{d.decisions.approval_rate_30d}%</div>
            <div className="text-xs text-slate-500 mt-1">
              {d.decisions.approved_30d} approved · {d.decisions.rejected_30d} rejected
            </div>
            <div className="text-xs text-slate-500 mt-1">{d.decisions.this_week} decisions this week ({d.decisions.total} lifetime)</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><DollarSign size={14}/>Financial to COD</CardTitle></CardHeader>
          <CardContent className="text-sm">
            <div className="text-3xl font-serif text-slate-900">{d.financial.new_this_month}</div>
            <div className="text-xs text-slate-500 mt-1">new items benefiting COD this month</div>
            <div className="text-xs text-slate-500 mt-1">{d.financial.pending} pending · {d.financial.approved} approved</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Top Priority Items</CardTitle></CardHeader>
          <CardContent className="space-y-2" data-testid="oic-exec-top-priority">
            {(d.top_priority || []).length === 0 && <div className="text-sm text-slate-500">Nothing above threshold right now.</div>}
            {(d.top_priority || []).map(o => (
              <Link key={o.id} to={`/staff/intelligence/${o.id}`} className="block rounded border border-slate-200 p-3 hover:border-[var(--cod-red)]">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${URGENCY_STYLES[o.urgency_level] || URGENCY_STYLES.green}`}>{o.urgency_level}</span>
                  <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-slate-100">{o.opportunity_type}</span>
                </div>
                <div className="font-medium text-slate-900 text-sm">{o.article_headline}</div>
                <div className="text-xs text-slate-500 mt-1">
                  Opp {o.opportunity_score} · Strategic {o.strategic_alignment_score || 0}
                  {o.opportunity_type === "Grant" && <> · Pursuit {o.grant_pursuit_score}</>}
                  {o.deadline_at && <> · Deadline {String(o.deadline_at).slice(0,10)}</>}
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Imminent Grant Deadlines (30d)</CardTitle></CardHeader>
          <CardContent className="space-y-2" data-testid="oic-exec-imminent">
            {(d.imminent_deadlines || []).length === 0 && <div className="text-sm text-slate-500">No grant deadlines in the next 30 days.</div>}
            {(d.imminent_deadlines || []).map(o => (
              <Link key={o.id} to={`/staff/intelligence/${o.id}`} className="block rounded border border-slate-200 p-3 hover:border-[var(--cod-red)]">
                <div className="font-medium text-slate-900 text-sm">{o.article_headline}</div>
                <div className="text-xs text-slate-600 mt-1">
                  Due <strong>{String(o.deadline_at).slice(0,10)}</strong>
                  {o.funding_amount && <> · {o.funding_amount}</>}
                  <> · Pursuit {o.grant_pursuit_score}</>
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Target size={14}/>Strategic Alignment Leaderboard (30d)</CardTitle></CardHeader>
        <CardContent className="space-y-2" data-testid="oic-exec-leaderboard">
          {(d.strategic_leaderboard || []).length === 0 && <div className="text-sm text-slate-500">No items with strategic score ≥ 70 in the last 30 days.</div>}
          {(d.strategic_leaderboard || []).map(o => (
            <Link key={o.id} to={`/staff/intelligence/${o.id}`} className="flex items-center gap-3 rounded border border-slate-200 p-3 hover:border-[var(--cod-red)]">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[var(--cod-navy)]/10 text-[var(--cod-navy)] font-serif text-lg flex items-center justify-center">{o.strategic_alignment_score}</div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 text-sm truncate">{o.article_headline}</div>
                <div className="text-xs text-slate-500">{o.opportunity_type} · Opp {o.opportunity_score}</div>
              </div>
            </Link>
          ))}
        </CardContent>
      </Card>
    </AppShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Institutional Memory
// ═══════════════════════════════════════════════════════════════════════════
export function IntelligenceInstitutionalMemory() {
  const [m, setM] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  useEffect(() => {
    const params = typeFilter !== "all" ? { opportunity_type: typeFilter } : {};
    oicApi.institutionalMemory(params).then(setM);
  }, [typeFilter]);
  if (!m) return <AppShell><div className="text-sm text-slate-500">Loading…</div></AppShell>;
  return (
    <AppShell>
      <Seo title="OIC · Institutional Memory" path="/staff/intelligence/institutional-memory" noindex/>
      <Link to="/staff/intelligence" className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-3"><ArrowLeft size={12}/> Intelligence Center</Link>
      <h1 className="font-serif text-2xl text-slate-900 flex items-center gap-2"><History size={20} className="text-[var(--cod-navy)]"/> Institutional Memory</h1>
      <div className="cod-rule-red mt-3 mb-4"/>
      <p className="text-sm text-slate-600 mb-4 max-w-3xl">
        A permanent log of every decision COD has made on incoming opportunities. This trains the AI to prioritize similar items and prevents duplicate pursuits. Total decisions: <strong>{m.total_decisions}</strong>.
      </p>

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Label className="text-xs">Filter type:</Label>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-52" data-testid="oic-memory-type-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="Grant">Grant</SelectItem>
            <SelectItem value="Procurement">Procurement</SelectItem>
            <SelectItem value="Employer Expansion">Employer Expansion</SelectItem>
            <SelectItem value="Hiring">Hiring</SelectItem>
            <SelectItem value="Workforce">Workforce</SelectItem>
            <SelectItem value="Business Development">Business Development</SelectItem>
            <SelectItem value="Economic Development">Economic Development</SelectItem>
            <SelectItem value="Community Event">Community Event</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-2" data-testid="oic-memory-timeline">
          {m.decisions.length === 0 && <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded p-6 text-center">No decisions logged yet.</div>}
          {m.decisions.map(d => (
            <div key={d.id} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${d.action === "approve" ? "bg-emerald-100 text-emerald-800" : d.action === "reject" ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-700"}`}>{d.action}</span>
                <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-700">{d.opportunity_type || "?"}</span>
                <span className="text-xs text-slate-500">{String(d.created_at).slice(0,10)}</span>
              </div>
              <div className="font-medium text-slate-900 text-sm">
                {d.opportunity ? (
                  <Link to={`/staff/intelligence/${d.opportunity.id}`} className="hover:text-[var(--cod-red)]">
                    {d.opportunity.article_headline}
                  </Link>
                ) : "(Opportunity removed)"}
              </div>
              <div className="text-xs text-slate-600 mt-1">
                Opp {d.opportunity_score ?? "—"} · Strategic {d.strategic_alignment_score ?? "—"}
                {d.agency && <> · {d.agency}</>}
                {d.funding_amount && <> · {d.funding_amount}</>}
              </div>
              {d.reason && <div className="text-xs text-slate-700 mt-1"><strong>Reason:</strong> {d.reason}</div>}
              {d.notes && <div className="text-xs text-slate-500 mt-1 italic">&ldquo;{d.notes}&rdquo;</div>}
            </div>
          ))}
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Pursuit Patterns</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1" data-testid="oic-memory-patterns">
            {m.pursuit_patterns.length === 0 && <div className="text-slate-500">No approval patterns yet.</div>}
            {m.pursuit_patterns.map((p, i) => (
              <div key={i} className="flex items-center justify-between border-b border-slate-100 py-1">
                <div className="min-w-0 flex-1 truncate">
                  <span className="font-medium">{p.opportunity_type || "?"}</span>
                  {p.agency && <span className="text-xs text-slate-500 ml-1">· {p.agency}</span>}
                </div>
                <span className="text-slate-900 font-semibold">{p.count}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Forecast
// ═══════════════════════════════════════════════════════════════════════════
function ForecastDueList({ items, testId }) {
  return (
    <div className="space-y-1" data-testid={testId}>
      {items.length === 0 && <div className="text-sm text-slate-500">Nothing due in this window.</div>}
      {items.map(o => (
        <Link key={o.id} to={`/staff/intelligence/${o.id}`} className="block rounded border border-slate-200 p-2 hover:border-[var(--cod-red)] text-xs">
          <div className="font-medium text-slate-900 truncate">{o.article_headline}</div>
          <div className="text-slate-500">{String(o.deadline_at).slice(0,10)} · {o.opportunity_type}
            {o.funding_amount && <> · {o.funding_amount}</>}</div>
        </Link>
      ))}
    </div>
  );
}

export function IntelligenceForecast() {
  const [f, setF] = useState(null);
  useEffect(() => { oicApi.forecast().then(setF); }, []);
  if (!f) return <AppShell><div className="text-sm text-slate-500">Loading…</div></AppShell>;
  return (
    <AppShell>
      <Seo title="OIC · Forecast" path="/staff/intelligence/forecast" noindex/>
      <Link to="/staff/intelligence" className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-3"><ArrowLeft size={12}/> Intelligence Center</Link>
      <h1 className="font-serif text-2xl text-slate-900 flex items-center gap-2"><CalendarClock size={20} className="text-[var(--cod-navy)]"/> Predictive Forecast</h1>
      <div className="cod-rule-red mt-3 mb-4"/>
      <p className="text-sm text-slate-600 mb-4 max-w-3xl">Upcoming deadlines, source velocity, and opportunity-type momentum. Use this to plan capacity and prioritize outreach.</p>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2 text-red-700"><AlertTriangle size={14}/>Due in 7 days ({f.deadlines_7d.length})</CardTitle></CardHeader>
          <CardContent><ForecastDueList items={f.deadlines_7d} testId="oic-forecast-7d"/></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2 text-amber-700"><AlertTriangle size={14}/>Due in 30 days ({f.deadlines_30d.length})</CardTitle></CardHeader>
          <CardContent><ForecastDueList items={f.deadlines_30d} testId="oic-forecast-30d"/></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2 text-slate-700"><CalendarClock size={14}/>Due in 60 days ({f.deadlines_60d.length})</CardTitle></CardHeader>
          <CardContent><ForecastDueList items={f.deadlines_60d} testId="oic-forecast-60d"/></CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><LineChart size={14}/>Source Velocity (30d)</CardTitle></CardHeader>
          <CardContent data-testid="oic-forecast-velocity">
            {f.source_velocity_30d.length === 0 && <div className="text-sm text-slate-500">No data yet.</div>}
            {f.source_velocity_30d.map((s, i) => (
              <div key={i} className="flex items-center gap-3 mb-2">
                <span className="text-xs text-slate-700 w-40 truncate">{s.source}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[var(--cod-navy)]" style={{ width: `${Math.min(100, s.count_30d * 10)}%` }}/>
                </div>
                <span className="text-xs text-slate-800 w-16 text-right">{s.count_30d} · avg {s.avg_score}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Type Momentum (last 4 weeks)</CardTitle></CardHeader>
          <CardContent data-testid="oic-forecast-momentum">
            {Object.keys(f.type_momentum_4wk).length === 0 && <div className="text-sm text-slate-500">No data yet.</div>}
            {Object.entries(f.type_momentum_4wk).map(([t, wk]) => (
              <div key={t} className="text-xs mb-2 border-b border-slate-100 pb-1">
                <div className="font-medium text-slate-900 mb-1">{t}</div>
                <div className="flex gap-2 text-slate-600">
                  <span>W1: <strong>{wk.week_1}</strong></span>
                  <span>W2: <strong>{wk.week_2}</strong></span>
                  <span>W3: <strong>{wk.week_3}</strong></span>
                  <span>W4: <strong>{wk.week_4}</strong></span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// AI Executive Brief
// ═══════════════════════════════════════════════════════════════════════════
export function IntelligenceExecBrief() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { oicApi.execBrief().then(setB).catch(() => {}); }, []);
  const generate = async () => {
    setBusy(true);
    try {
      const doc = await oicApi.generateExecBrief();
      setB(doc);
      toast.success("Executive brief generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Generate failed"); }
    finally { setBusy(false); }
  };
  const brief = b?.brief;
  return (
    <AppShell>
      <Seo title="OIC · AI Executive Brief" path="/staff/intelligence/exec-brief" noindex/>
      <Link to="/staff/intelligence" className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-3"><ArrowLeft size={12}/> Intelligence Center</Link>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 className="font-serif text-2xl text-slate-900 flex items-center gap-2"><Sparkles size={20} className="text-[var(--cod-red)]"/> AI Executive Brief</h1>
          <div className="cod-rule-red mt-3"/>
          <p className="text-sm text-slate-600 mt-3 max-w-3xl">Weekly Claude-generated narrative summarizing COD&apos;s institutional intelligence pipeline for leadership.</p>
        </div>
        <Button className="cod-bg-red" onClick={generate} disabled={busy} data-testid="oic-generate-brief">
          {busy ? <><RefreshCw size={14} className="mr-1 animate-spin"/>Generating…</> : <><Sparkles size={14} className="mr-1"/>Generate New Brief</>}
        </Button>
      </div>

      {!brief && (
        <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded p-8 text-center">
          No brief generated yet. Click &ldquo;Generate New Brief&rdquo; to create the first one.
        </div>
      )}

      {brief && (
        <div className="space-y-4" data-testid="oic-exec-brief">
          <Card>
            <CardContent className="pt-6">
              <div className="text-xs uppercase tracking-widest text-slate-500">Week ending {b?.metrics?.week_ending} · Generated {String(b?.created_at || "").slice(0,16).replace("T"," ")}</div>
              <h2 className="font-serif text-xl text-slate-900 mt-2 leading-snug" data-testid="oic-brief-headline">{brief.headline}</h2>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2 text-emerald-700"><CheckCircle2 size={14}/>Top Wins</CardTitle></CardHeader>
              <CardContent><ul className="text-sm text-slate-800 space-y-2 list-disc list-inside">{(brief.top_wins || []).map((w,i)=><li key={i}>{w}</li>)}</ul></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2 text-red-700"><Zap size={14}/>Priority Actions</CardTitle></CardHeader>
              <CardContent><ol className="text-sm text-slate-800 space-y-2 list-decimal list-inside">{(brief.priority_actions || []).map((a,i)=><li key={i}>{a}</li>)}</ol></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2 text-amber-700"><AlertTriangle size={14}/>Watchlist</CardTitle></CardHeader>
              <CardContent><ul className="text-sm text-slate-800 space-y-2 list-disc list-inside">{(brief.watchlist || []).map((w,i)=><li key={i}>{w}</li>)}</ul></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2 text-slate-700"><TrendingUp size={14}/>Trends</CardTitle></CardHeader>
              <CardContent><ul className="text-sm text-slate-800 space-y-2 list-disc list-inside">{(brief.trend_observations || []).map((w,i)=><li key={i}>{w}</li>)}</ul></CardContent>
            </Card>
          </div>

          {brief.closing && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-slate-700 italic border-l-2 border-[var(--cod-red)] pl-3" data-testid="oic-brief-closing">{brief.closing}</p>
              </CardContent>
            </Card>
          )}

          {b?.metrics && (
            <Card>
              <CardHeader><CardTitle className="text-base">Underlying metrics</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {Object.entries(b.metrics).map(([k,v])=>(
                    <div key={k} className="border border-slate-100 rounded p-2">
                      <div className="text-[10px] uppercase text-slate-500">{k.replace(/_/g,' ')}</div>
                      <div className="font-serif text-lg text-slate-900">{String(v)}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </AppShell>
  );
}
